import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: null = checking, false = not auth, object = authed
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Login bloccato per maintenance mode: normalizza risposta con flag
      if (
        status === 503 &&
        typeof detail === "object" &&
        detail?.maintenance === true
      ) {
        return {
          ok: false,
          maintenance: true,
          error:
            "Sistema in manutenzione. Solo l'owner può accedere ora.",
          message: detail?.message || "",
        };
      }
      return { ok: false, error: formatApiError(err) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}

// Role helpers
export const ROLE_LABELS = {
  super_admin: "Super Admin",
  capo_area: "Capo Area",
  resident_manager: "Resident Manager",
  housekeeping_manager: "Housekeeping Manager",
  front_office_manager: "Front Office Manager",
  read_only: "Sola Lettura",
};

export function roleLabel(role) {
  return ROLE_LABELS[role] || role;
}
